import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { AutoComplete, Spin } from 'antd';
import type { AutoCompleteProps } from 'antd';
import { useList, useOne } from '@refinedev/core';
import { buildPatientSearchFilters } from '@src/utils/patientSearchFilters';

export interface AutoCompleteFieldProps
  extends Omit<AutoCompleteProps<any>, 'options' | 'children'> {
  resource: string;
  optionValue: string;
  searchFields: string[];
  searchDebounceMs: number;
  filters: any[];
  sorters: any[];
  pagination: {
    pageSize: number;
  };
  queryOptions: {
    enabled: boolean;
    staleTime: number;
    cacheTime: number;
    refetchOnWindowFocus: boolean;
    refetchOnMount: boolean;
  };
  initialValue: any;
  optionRender: (option: any) => React.ReactNode;
  required?: boolean;
  status?: 'error' | 'warning';
}

const DEFAULT_QUERY_OPTIONS = {
  enabled: true,
  staleTime: 10 * 60 * 1000,
  cacheTime: 30 * 60 * 1000,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
};

const DEFAULT_PAGINATION = { pageSize: 10 };

function AutoCompleteField({
  resource,
  optionValue = 'id',
  searchFields,
  searchDebounceMs = 300,
  filters = [],
  sorters = [],
  pagination = DEFAULT_PAGINATION,
  queryOptions = DEFAULT_QUERY_OPTIONS,
  value,
  onChange,
  initialValue,
  optionRender,
  required = false,
  status,
  ...props
}: AutoCompleteFieldProps) {
  // State management
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [internalValue, setInternalValue] = useState('');
  const currentSearchRef = useRef<string>(''); // Track current search to prevent stale results

  // Memoized values and computations
  const memoizedValues = useMemo(() => {
    // Extract initial value ID
    const val = initialValue || value;
    const initialValueId = !val ? null : 
      typeof val === 'object' && val.hasOwnProperty('value') ? val.value : val;

    // Query options for initial value fetching
    const initialQueryOptions = {
      ...queryOptions,
      enabled: !!initialValueId && 
                (typeof initialValueId === 'number') && 
                !debouncedSearchValue, // Don't fetch initial data when searching
    };

    // Filter generation
    const allFilters = [
      ...filters,
      ...(debouncedSearchValue && searchFields.length > 0
        ? resource === 'patients'
          ? buildPatientSearchFilters(debouncedSearchValue, searchFields)
          : searchFields.map(field => ({
              field,
              operator: 'contains' as const,
              value: debouncedSearchValue,
            }))
        : []),
    ];

    // Query options and parameters for data fetching
    // For search queries, use shorter cache times to ensure fresh results
    // For non-search queries, use longer cache times
    const listQueryOptions = {
      ...queryOptions,
      enabled: true,
      // When searching, reduce staleTime to ensure fresh results
      staleTime: debouncedSearchValue 
        ? Math.min(queryOptions.staleTime || 0, 30 * 1000) // Max 30 seconds for search results
        : queryOptions.staleTime,
    };
    const memoizedPagination = pagination;
    const memoizedSorters = sorters.length > 0 ? sorters : undefined;
    const memoizedFilters = allFilters.length > 0 ? allFilters : undefined;

    return {
      initialValueId,
      initialQueryOptions,
      allFilters,
      listQueryOptions,
      memoizedPagination,
      memoizedSorters,
      memoizedFilters,
    };
  }, [
    initialValue,
    value,
    queryOptions,
    debouncedSearchValue,
    filters,
    searchFields,
    pagination,
    sorters,
    resource,
  ]);

  // Data fetching hooks
  const { data: initialData, isLoading: initialLoading } = useOne({
    resource,
    id: memoizedValues.initialValueId,
    queryOptions: memoizedValues.initialQueryOptions,
  });

  const { data: listData, isLoading: listLoading } = useList({
    resource,
    filters: memoizedValues.memoizedFilters,
    sorters: memoizedValues.memoizedSorters,
    pagination: memoizedValues.memoizedPagination,
    queryOptions: memoizedValues.listQueryOptions,
  });

  // Update current search ref when debounced value changes
  useEffect(() => {
    currentSearchRef.current = debouncedSearchValue;
  }, [debouncedSearchValue]);

  // Memoized data processing
  const processedData = useMemo(() => {
    // Only process data if it matches the current search (prevent stale results)
    // This ensures we don't show results from a previous search
    const searchData = listData?.data || [];
    const seenValues = new Set<string | number>();
    const options: Array<{ label: React.ReactNode; value: any; data: any }> = [];
    
    // First, add search results (most recent data)
    // Deduplicate by value to prevent showing the same option multiple times
    searchData.forEach((item: any) => {
      const itemValue = item[optionValue];
      // Use a unique key for deduplication
      const uniqueKey = typeof itemValue === 'string' || typeof itemValue === 'number' 
        ? itemValue 
        : JSON.stringify(itemValue);
      
      if (!seenValues.has(uniqueKey)) {
        seenValues.add(uniqueKey);
        options.push({
          label: optionRender(item),
          value: itemValue,
          data: item,
        });
      }
    });

    // Add initial data if it exists and isn't already in the search results
    // Only add initial data if we're not actively searching (no debouncedSearchValue)
    if (initialData?.data && !debouncedSearchValue) {
      const initialOption = {
        label: optionRender(initialData.data),
        value: initialData.data[optionValue],
        data: initialData.data,
      };
      
      // Check if initial option already exists in search results
      if (!seenValues.has(initialOption.value)) {
        options.unshift(initialOption);
      }
    }

    // Get display value
    const displayValue = (() => {
      if (value) {
        let searchValue = value;
        
        // Extract the actual value if it's an object
        if (typeof value === 'object' && value.value !== undefined) {
          searchValue = value.value;
        }
        
        // Try to find the option in options
        const option = options.find(opt => opt.value === searchValue);
        if (option) {
          return option.label;
        }
        
        // If not found in options but we have the value, return the value itself
        return searchValue;
      }
      
      // If we have initial data and no current value, show initial data label
      if (initialData?.data && !value) {
        return optionRender(initialData.data);
      }
      
      return '';
    })();

    return {
      options,
      displayValue,
    };
  }, [
    listData?.data,
    initialData?.data,
    optionRender,
    optionValue,
    value,
    debouncedSearchValue, // Include debouncedSearchValue to ensure fresh results
  ]);

  // Loading state
  const showLoading = useMemo(() => !!listLoading, [listLoading]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, searchDebounceMs);

    return () => clearTimeout(timer);
  }, [searchValue, searchDebounceMs]);

  // Update internal value when display value changes
  useEffect(() => {
    if (processedData.displayValue !== internalValue) {
      setInternalValue(processedData.displayValue);
    }
  }, [processedData.displayValue, internalValue]);

  // Event handlers
  const handleChange = useCallback((newValue: any) => {
    if (!onChange) return;
    
    // Prevent infinite loop by checking if value actually changed
    const currentValue = value;
    const newTypedValue = !newValue ? null : { 
      value: newValue, 
      data: processedData.options.find(opt => opt.value === newValue)?.data || null 
    };

    // Deep comparison to prevent unnecessary updates
    const hasChanged = JSON.stringify(currentValue) !== JSON.stringify(newTypedValue);
    
    if (hasChanged) {
      onChange(newTypedValue);
    }
  }, [onChange, processedData.options, value]);

  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const handleSelect = useCallback((value: any, option: any) => {
    if (!onChange) return;
    
    // Pass the selected value with data
    const selectedValue = { 
      value: value, 
      data: option?.data || null 
    };
    
    // Prevent infinite loop by checking if value actually changed
    const currentValue = value;
    const hasChanged = JSON.stringify(currentValue) !== JSON.stringify(selectedValue);
    
    if (hasChanged) {
      onChange(selectedValue);
    }
  }, [onChange, value]);

  // Memoized props and content
  const memoizedProps = useMemo(() => {
    const { allowClear, ...restProps } = props;
    return restProps;
  }, [props]);

  const notFoundContent = useMemo(() => 
    showLoading ? <Spin size="small" /> : 'No results found', 
    [showLoading]
  );

  const optionRenderCallback = useCallback((option: any) => (
    <div>{option.label}</div>
  ), []);

  return (
    <AutoComplete
      filterOption={false}
      allowClear={props.allowClear !== false}
      notFoundContent={notFoundContent}
      status={status}
      {...memoizedProps}
      onSearch={handleSearch}
      options={processedData.options}
      value={internalValue}
      onChange={handleChange}
      onSelect={handleSelect}
      optionRender={optionRenderCallback}
      backfill={false}
    />
  );
}

export { AutoCompleteField }; 